import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useId,
  useCallback,
  memo,
} from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  PaginationState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleX,
  Columns3,
  Ellipsis,
  Plus,
  Trash2,
  Phone,
  Mail,
  Calendar,
  Search,
  Filter,
  ListFilter,
  Building,
  Shield,
  User,
  Edit,
  MoreHorizontal,
  CheckCircle,
  AlertCircle,
  X,
  Loader2Icon,
} from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { api } from "../../services/api";
import { Loader } from "../ui/loader";
import UserFilterDrawer, { UserFilters } from "./UserFilterDrawer";
import { withMask } from "use-mask-input";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../store";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  college: {
    id: string;
    name: string;
    code: string;
  } | null;
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
}

// User Form Validation Schema
const UserFormSchema = z
  .object({
    firstName: z
      .string()
      .min(1, { message: "First name is required" })
      .min(2, { message: "First name must be at least 2 characters" })
      .max(50, { message: "First name must not exceed 50 characters" })
      .regex(/^[a-zA-Z\s]+$/, {
        message: "First name can only contain letters and spaces",
      }),
    lastName: z
      .string()
      .min(1, { message: "Last name is required" })
      .max(50, { message: "Last name must not exceed 50 characters" })
      .regex(/^[a-zA-Z\s]+$/, {
        message: "Last name can only contain letters and spaces",
      }),
    email: z
      .string()
      .min(1, { message: "Email is required" })
      .email({ message: "Please enter a valid email address" }),
    phone: z
      .string()
      .optional()
      .refine((val) => !val || /^[\+]?[0-9\s\-\(\)]{10,15}$/.test(val), {
        message: "Please enter a valid phone number",
      }),
    role: z.string().min(1, { message: "Role is required" }),
    collegeId: z.string().optional(),
    isActive: z.boolean(),
  })
  .refine(
    (data) => {
      // If role is not SYSTEM_ADMIN, collegeId is required
      if (data.role !== "SYSTEM_ADMIN" && !data.collegeId) {
        return false;
      }
      return true;
    },
    {
      message: "College is required for this role",
      path: ["collegeId"],
    }
  );

type UserFormData = z.infer<typeof UserFormSchema>;

// Custom filter function for multi-column searching
const multiColumnFilterFn: FilterFn<User> = (row, columnId, filterValue) => {
  const searchableRowContent = `${row.original.firstName} ${
    row.original.lastName
  } ${row.original.email} ${row.original.college?.name || ""}`.toLowerCase();
  const searchTerm = (filterValue ?? "").toLowerCase();
  return searchableRowContent.includes(searchTerm);
};

const statusFilterFn: FilterFn<User> = (
  row,
  columnId,
  filterValue: string[]
) => {
  if (!filterValue?.length) return true;
  const status = row.getValue(columnId) as boolean;
  const statusString = status ? "Active" : "Inactive";
  return filterValue.includes(statusString);
};

// Row actions component
interface RowActionsProps {
  row: Row<User>;
  onEdit: (user: User) => void;
  onRefresh: () => void;
  onAlert: (type: "success" | "error", title: string, message: string) => void;
}

const RowActions: React.FC<RowActionsProps> = ({
  row,
  onEdit,
  onRefresh,
  onAlert,
}) => {
  const user = row.original;

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${user.id}`);

      // Show success alert
      onAlert(
        "success",
        "User Deleted Successfully",
        `${user.firstName} ${user.lastName} has been deleted successfully.`
      );

      onRefresh();
    } catch (error: any) {
      console.error("Failed to delete user:", error);

      // Show error alert
      onAlert(
        "error",
        "Delete Failed",
        error.response?.data?.message ||
          "Failed to delete user. Please try again."
      );
    }
  };

  return (
    <div className="flex justify-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 p-0"
            aria-label="Edit item"
          >
            <Ellipsis className="stroke-2 h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[160px]">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => onEdit(user)}>
              Edit
            </DropdownMenuItem>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="text-destructive focus:text-destructive"
                  disabled={user.role === "SYSTEM_ADMIN"}
                >
                  Delete
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    the user account for {user.firstName} {user.lastName}.
                    {user.role === "SYSTEM_ADMIN" && (
                      <span className="block mt-2 text-amber-600 font-medium">
                        System administrators cannot be deleted.
                      </span>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={user.role === "SYSTEM_ADMIN"}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Memoized search input component to prevent focus loss
interface SearchInputProps {
  id: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  searchInProgress: boolean;
}

const SearchInput = memo<SearchInputProps>(
  ({ id, searchTerm, onSearchChange, onSearchClear, searchInProgress }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    return (
      <div className="relative bg-white flex-1">
        <Input
          id={`${id}-input`}
          ref={inputRef}
          className={cn("ps-9", Boolean(searchTerm) && "pe-9")}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Filter by name, email, role, or college..."
          type="text"
          aria-label="Filter by name, email, role, or college"
        />
        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
          {searchInProgress ? (
            <Loader2Icon className="animate-spin h-4 w-4" />
          ) : (
            <Search className="stroke-2 h-4 w-4" aria-hidden="true" />
          )}
        </div>
        {Boolean(searchTerm) && (
          <button
            className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 outline-offset-2 transition-colors hover:text-foreground focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Clear filter"
            onClick={onSearchClear}
          >
            <CircleX className="stroke-2 h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }
);

const Users = () => {
  const id = useId();

  // Get current user from auth state
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const isCollegeAdmin = currentUser?.role === "COLLEGE_ADMIN";

  // Local state management (no longer using Redux for filtered results)
  const [users, setUsers] = useState<User[]>([]);
  const [localPagination, setLocalPagination] = useState({
    total: 0,
    totalPages: 0,
    page: 1,
    limit: 10,
  });
  const [isLoading, setIsLoading] = useState(false);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "firstName",
      desc: false,
    },
  ]);

  // Add search state for server-side search
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Add a separate state to track if we're in the middle of a search operation
  // This prevents unnecessary re-renders that cause input focus loss
  const [searchInProgress, setSearchInProgress] = useState(false);

  // Filter drawer state
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<UserFilters>({
    status: [],
    role: [],
    collegeId: [],
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [colleges, setColleges] = useState<any[]>([]);

  // Alert state for showing success/error messages
  const [alert, setAlert] = useState<{
    show: boolean;
    type: "success" | "error";
    title: string;
    message: string;
  }>({
    show: false,
    type: "success",
    title: "",
    message: "",
  });

  // Helper function to show alert messages
  const showAlert = (
    type: "success" | "error",
    title: string,
    message: string
  ) => {
    setAlert({
      show: true,
      type,
      title,
      message,
    });

    // Auto-hide success alerts after 5 seconds
    if (type === "success") {
      setTimeout(() => {
        setAlert((prev) => ({ ...prev, show: false }));
      }, 5000);
    }
  };

  // Helper function to hide alert
  const hideAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  // Form validation using react-hook-form with Zod
  const createForm = useForm<UserFormData>({
    resolver: zodResolver(UserFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "",
      collegeId: currentUser?.college?.id,
      isActive: true,
    },
  });

  const editForm = useForm<UserFormData>({
    resolver: zodResolver(UserFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "",
      collegeId: "",
      isActive: true,
    },
  });

  // Fetch colleges for dropdown
  const fetchColleges = async () => {
    try {
      const response = await api.get("/colleges", {
        params: { limit: 100 }, // Get all colleges
      });
      if (response.data?.data) {
        setColleges(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch colleges:", error);
    }
  };

  // Load colleges when component mounts (only for system admins)
  useEffect(() => {
    if (!isCollegeAdmin) {
      fetchColleges();
    }
  }, [isCollegeAdmin]);

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    editForm.reset({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone || "",
      role: user.role,
      collegeId: user.college?.id || "",
      isActive: user.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateUser = async (data: UserFormData) => {
    if (!editingUser) return;

    setIsSubmitting(true);

    try {
      await api.put(`/users/${editingUser.id}`, data);

      editForm.reset();
      setIsEditDialogOpen(false);
      setEditingUser(null);

      // Show success alert
      showAlert(
        "success",
        "User Updated Successfully",
        `${data.firstName} ${data.lastName} has been updated successfully.`
      );

      // Refresh data using filter endpoint
      refreshData();
    } catch (error: any) {
      console.error("Failed to update user:", error);

      // Show error alert
      showAlert(
        "error",
        "Failed to Update User",
        error.response?.data?.message ||
          "Failed to update user information. Please check the information and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateUser = async (data: UserFormData) => {
    setIsSubmitting(true);

    try {
      // Prepare data for submission - remove collegeId for SYSTEM_ADMIN
      const submitData = {
        ...data,
        ...(data.role === "SYSTEM_ADMIN" && { collegeId: undefined }),
      };

      await api.post("/users", submitData);

      createForm.reset();
      setIsCreateDialogOpen(false);

      // Show success alert
      showAlert(
        "success",
        "User Created Successfully",
        `${data.firstName} ${data.lastName} has been created successfully.`
      );

      // Refresh data using filter endpoint
      refreshData();
    } catch (error: any) {
      console.error("Failed to create user:", error);

      // Show error alert
      showAlert(
        "error",
        "Failed to Create User",
        error.response?.data?.message ||
          "Failed to create user. Please check the information and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRows = () => {
    const selectedRows = table.getSelectedRowModel().rows;
    const selectedUsers = selectedRows.map((row) => row.original);
    const userIds = selectedUsers.map((user) => user.id);

    // Use the new bulk delete endpoint - automatically handles single or bulk operations
    const idsParam = userIds.join(",");

    api
      .delete(`/users/${idsParam}`)
      .then(() => {
        // Refresh data using the filter endpoint instead of Redux
        const hasActiveFilters =
          activeFilters.status.length > 0 ||
          activeFilters.role.length > 0 ||
          activeFilters.collegeId.length > 0;

        const filterParams: any = {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search: debouncedSearchTerm || undefined,
        };

        // Add filter parameters if any filters are active
        if (hasActiveFilters) {
          if (activeFilters.status.length > 0) {
            filterParams.status = activeFilters.status.join(",");
          }
          if (activeFilters.role.length > 0) {
            filterParams.role = activeFilters.role.join(",");
          }
          if (activeFilters.collegeId.length > 0) {
            filterParams.collegeId = activeFilters.collegeId.join(",");
          }
        }

        setIsLoading(true);
        return api.get("/users/filter", { params: filterParams });
      })
      .then((response) => {
        if (response) {
          const { data, meta: responsePagination } = response.data;
          setUsers(data || []);
          setLocalPagination({
            total: responsePagination?.total || 0,
            totalPages: responsePagination?.totalPages || 0,
            page: responsePagination?.page || 1,
            limit: responsePagination?.limit || 10,
          });
        }
        table.resetRowSelection();
      })
      .catch((error) => {
        console.error("Failed to delete users:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Function to refresh data using filter endpoint
  const refreshData = async () => {
    const hasActiveFilters =
      activeFilters.status.length > 0 ||
      activeFilters.role.length > 0 ||
      activeFilters.collegeId.length > 0;

    const filterParams: any = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm || undefined,
    };

    // Add filter parameters if any filters are active
    if (hasActiveFilters) {
      if (activeFilters.status.length > 0) {
        filterParams.status = activeFilters.status.join(",");
      }
      if (activeFilters.role.length > 0) {
        filterParams.role = activeFilters.role.join(",");
      }
      if (activeFilters.collegeId.length > 0) {
        filterParams.collegeId = activeFilters.collegeId.join(",");
      }
    }

    setIsLoading(true);

    try {
      const response = await api.get("/users/filter", {
        params: filterParams,
      });

      if (response) {
        const { data, meta: responsePagination } = response.data;
        setUsers(data || []);
        setLocalPagination({
          total: responsePagination?.total || 0,
          totalPages: responsePagination?.totalPages || 0,
          page: responsePagination?.page || 1,
          limit: responsePagination?.limit || 10,
        });
      }
    } catch (error) {
      console.error("Failed to fetch filtered users:", error);
      setUsers([]);
      setLocalPagination({
        total: 0,
        totalPages: 0,
        page: 1,
        limit: pagination.pageSize,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search term
  useEffect(() => {
    setSearchInProgress(true);
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setSearchInProgress(false);
    }, 300);

    return () => {
      clearTimeout(timer);
      setSearchInProgress(false);
    };
  }, [searchTerm, debouncedSearchTerm]);

  // Fetch data using filter endpoint
  useEffect(() => {
    const hasActiveFilters =
      activeFilters.status.length > 0 ||
      activeFilters.role.length > 0 ||
      activeFilters.collegeId.length > 0;

    const filterParams: any = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm || undefined,
    };

    // Add filter parameters if any filters are active
    if (hasActiveFilters) {
      if (activeFilters.status.length > 0) {
        filterParams.status = activeFilters.status.join(",");
      }
      if (activeFilters.role.length > 0) {
        filterParams.role = activeFilters.role.join(",");
      }
      if (activeFilters.collegeId.length > 0) {
        filterParams.collegeId = activeFilters.collegeId.join(",");
      }
    }

    setIsLoading(true);

    api
      .get("/users/filter", { params: filterParams })
      .then((response) => {
        const { data, meta: responsePagination } = response.data;
        setUsers(data || []);
        setLocalPagination({
          total: responsePagination?.total || 0,
          totalPages: responsePagination?.totalPages || 0,
          page: responsePagination?.page || 1,
          limit: responsePagination?.limit || 10,
        });
      })
      .catch((error) => {
        console.error("Failed to fetch filtered users:", error);
        setUsers([]);
        setLocalPagination({
          total: 0,
          totalPages: 0,
          page: 1,
          limit: pagination.pageSize,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearchTerm,
    activeFilters,
  ]);

  // Define table columns
  const allColumns: ColumnDef<User>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-0.5"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "firstName",
      header: "Name",
      cell: ({ row }) => {
        const user = row.original;
        return (
          <>
            <div className="font-medium text-gray-900">
              {user.firstName} {user.lastName}
            </div>
            <div className="text-sm text-gray-500">{user.email}</div>
          </>
        );
      },
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => {
        const role = row.original.role;
        const getRoleBadgeColor = (role: string) => {
          switch (role) {
            case "SYSTEM_ADMIN":
              return "bg-red-100 text-red-800";
            case "COLLEGE_ADMIN":
              return "bg-blue-100 text-blue-800";
            case "ACCOUNTANT":
              return "bg-green-100 text-green-800";
            case "STAFF":
              return "bg-gray-100 text-gray-800";
            case "FACULTY":
              return "bg-purple-100 text-purple-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        };

        const formatRole = (role: string) => {
          return role.replace(/_/g, " ");
        };

        return (
          <Badge
            variant="outline"
            className={cn("border-none", getRoleBadgeColor(role))}
          >
            {formatRole(role)}
          </Badge>
        );
      },
    },
    {
      accessorKey: "college",
      header: "College",
      cell: ({ row }) => {
        const college = row.original.college;
        return college ? (
          <>
            <div className="font-medium text-gray-900">{college.name}</div>
            <div className="text-sm text-gray-500">{college.code}</div>
          </>
        ) : (
          <span>System Level</span>
        );
      },
    },
    {
      accessorKey: "phone",
      header: "Contact",
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="space-y-1 flex flex-col">
            <span
              className="text-sm text-gray-800"
              style={{ wordBreak: "break-word" }}
            >
              {user.email}
            </span>
            {user.phone && (
              <span
                className="text-sm text-gray-800"
                style={{ wordBreak: "break-word" }}
              >
                {user.phone}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "lastLogin",
      header: "Last Login",
      cell: ({ row }) => {
        const lastLogin = row.original.lastLogin;
        const formatLastLogin = (lastLogin: string | null) => {
          if (!lastLogin) return "Never";
          const date = new Date(lastLogin);
          const now = new Date();
          const diffInHours =
            (now.getTime() - date.getTime()) / (1000 * 60 * 60);

          if (diffInHours < 24) {
            return "Today";
          } else if (diffInHours < 48) {
            return "Yesterday";
          } else {
            return date.toLocaleDateString();
          }
        };

        return (
          <div className="flex items-center gap-1 text-gray-800">
            <Calendar className="h-3 w-3" />
            <span className="text-sm">{formatLastLogin(lastLogin)}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }) => {
        const isActive = row.original.isActive;
        return (
          <Badge
            className={cn(
              !isActive && "bg-muted-foreground/60 text-primary-foreground"
            )}
          >
            {isActive ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => (
        <RowActions
          row={row}
          onEdit={handleEditUser}
          onRefresh={refreshData}
          onAlert={showAlert}
        />
      ),
      size: 60,
      enableSorting: false,
      enableHiding: false,
    },
  ];

  // Filter out college column for college admins
  const columns = isCollegeAdmin
    ? allColumns.filter((column) => (column as any).accessorKey !== "college")
    : allColumns;

  const table = useReactTable({
    data: users || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    // Configure for server-side pagination
    manualPagination: true,
    pageCount: localPagination?.totalPages || 0,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    // Remove client-side filtering since we use server-side search
    // getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility,
    },
  });

  // Use callback approach for search input to prevent re-renders
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Filter drawer handlers
  const handleApplyFilters = (filters: UserFilters) => {
    setActiveFilters(filters);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleOpenFilterDrawer = () => {
    setIsFilterDrawerOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setIsFilterDrawerOpen(false);
  };

  // Function to get active filter count for display
  const getActiveFilterCount = () => {
    return (
      activeFilters.status.length +
      activeFilters.role.length +
      activeFilters.collegeId.length
    );
  };

  return (
    <>
      {/* Filter Drawer */}
      <UserFilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={handleCloseFilterDrawer}
        onApplyFilters={handleApplyFilters}
        currentFilters={activeFilters}
        currentUserRole={currentUser?.role}
      />

      {/* Alert Messages */}
      {alert.show && (
        <div className="fixed top-4 right-4 z-50 w-96">
          <Alert
            variant={alert.type === "error" ? "destructive" : "default"}
            className={cn(
              "border shadow-lg",
              alert.type === "success" &&
                "border-green-200 bg-green-50 text-green-800",
              alert.type === "error" && "border-red-200 bg-red-50"
            )}
          >
            <div className="flex items-start gap-3">
              {alert.type === "success" ? (
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <AlertTitle>{alert.title}</AlertTitle>
                <AlertDescription className="mt-1">
                  {alert.message}
                </AlertDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-6 w-6 flex-shrink-0 hover:bg-transparent",
                  alert.type === "success"
                    ? "hover:text-green-900"
                    : "hover:text-red-900"
                )}
                onClick={hideAlert}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </Alert>
        </div>
      )}

      <div className="space-y-4 max-w-full">
        {/* Header Section - Single line with title + count on left, controls on right */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-gray-900">Users</h1>
            <Badge className="h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
              {localPagination?.total || 0}
            </Badge>
          </div>
          <div className="flex items-center gap-3 w-full md:w-1/2">
            {/* Search Input */}
            <SearchInput
              id={id}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSearchClear={handleSearchClear}
              searchInProgress={searchInProgress}
            />

            {/* Filter Button */}

            {/* Toggle columns visibility / Delete selected - Combined in single container */}
            <div className="flex items-center border rounded-md bg-white">
              {/* Filter Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleOpenFilterDrawer}
                className="relative rounded-r-none border-r"
              >
                <ListFilter className="opacity-60 h-4 w-4" />
                {getActiveFilterCount() > 0 && (
                  <Badge className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums text-xs">
                    {getActiveFilterCount()}
                  </Badge>
                )}
              </Button>
              {/* Column Toggle Icon */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      table.getSelectedRowModel().rows.length > 0 &&
                        "rounded-r-none border-r",
                      "rounded-l-none"
                    )}
                  >
                    <Columns3
                      className="opacity-60 h-4 w-4"
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="center"
                  className="w-56 p-3"
                >
                  <div className="space-y-3">
                    <div className="text-xs font-medium text-muted-foreground">
                      Toggle columns
                    </div>
                    <div className="space-y-4">
                      {table
                        .getAllColumns()
                        .filter((column) => column.getCanHide())
                        .map((column) => (
                          <div
                            key={column.id}
                            className="flex items-center gap-2"
                          >
                            <Checkbox
                              id={`${id}-column-${column.id}`}
                              checked={column.getIsVisible()}
                              onCheckedChange={(checked: boolean) =>
                                column.toggleVisibility(!!checked)
                              }
                            />
                            <Label
                              htmlFor={`${id}-column-${column.id}`}
                              className="flex grow justify-between gap-2 font-normal capitalize"
                            >
                              {column.id === "firstName"
                                ? "Name"
                                : column.id === "role"
                                ? "Role"
                                : column.id === "college"
                                ? "College"
                                : column.id === "phone"
                                ? "Contact"
                                : column.id === "lastLogin"
                                ? "Last login"
                                : column.id === "isActive"
                                ? "Status"
                                : column.id}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Delete Icon - Only show when rows are selected */}
              {table.getSelectedRowModel().rows.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative rounded-l-none text-red-500 hover:text-red-500"
                    >
                      <Trash2 className="stroke-2 h-4 w-4" aria-hidden="true" />
                      <Badge className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums text-xs bg-red-500 hover:bg-red-500">
                        {table.getSelectedRowModel().rows.length}
                      </Badge>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Are you absolutely sure?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently
                        delete {table.getSelectedRowModel().rows.length}{" "}
                        selected{" "}
                        {table.getSelectedRowModel().rows.length === 1
                          ? "user"
                          : "users"}
                        .
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDeleteRows}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>

            {/* Add User Button - Icon only */}
            <Dialog
              open={isCreateDialogOpen}
              onOpenChange={setIsCreateDialogOpen}
            >
              <DialogTrigger asChild>
                <Button size="icon">
                  <Plus className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create New User</DialogTitle>
                  <DialogDescription>
                    Add a new user to the system.
                  </DialogDescription>
                </DialogHeader>

                <Form {...createForm}>
                  <form
                    onSubmit={createForm.handleSubmit(handleCreateUser)}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 gap-4">
                      <FormField
                        control={createForm.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g., John" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={createForm.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g., Doe" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={createForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email *</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="e.g., john.doe@example.com"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={createForm.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="+91-8012345678"
                                {...field}
                                ref={withMask("+99-9999999999", {
                                  placeholder: "",
                                  showMaskOnHover: false,
                                })}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {currentUser?.role === "SYSTEM_ADMIN" && (
                        <FormField
                          control={createForm.control}
                          name="collegeId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {createForm.watch("role")}College *
                              </FormLabel>
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select college" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {colleges.map((college) => (
                                    <SelectItem
                                      key={college.id}
                                      value={college.id}
                                    >
                                      <div>
                                        <div>{college.name}</div>
                                        <div className="text-xs text-gray-500">
                                          {college.code}
                                        </div>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      <FormField
                        control={createForm.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Role *</FormLabel>
                            <Select
                              value={field.value}
                              onValueChange={(value) => {
                                field.onChange(value);
                                // Clear collegeId when switching to SYSTEM_ADMIN
                                if (value === "SYSTEM_ADMIN") {
                                  createForm.setValue("collegeId", "");
                                }
                              }}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="SYSTEM_ADMIN">
                                  System Admin
                                </SelectItem>
                                <SelectItem value="COLLEGE_ADMIN">
                                  College Admin
                                </SelectItem>
                                <SelectItem value="ACCOUNTANT">
                                  Accountant
                                </SelectItem>
                                <SelectItem value="STAFF">Staff</SelectItem>
                                <SelectItem value="FACULTY">Faculty</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsCreateDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Creating..." : "Create User"}
                      </Button>
                    </DialogFooter>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Edit User Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user information for {editingUser?.firstName}{" "}
                {editingUser?.lastName}.
              </DialogDescription>
            </DialogHeader>

            <Form {...editForm}>
              <form
                onSubmit={editForm.handleSubmit(handleUpdateUser)}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={editForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email *</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="e.g., john.doe@example.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., +91-9876543210"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role *</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);
                            // Clear collegeId when switching to SYSTEM_ADMIN
                            if (value === "SYSTEM_ADMIN") {
                              editForm.setValue("collegeId", "");
                            }
                          }}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="SYSTEM_ADMIN">
                              System Admin
                            </SelectItem>
                            <SelectItem value="COLLEGE_ADMIN">
                              College Admin
                            </SelectItem>
                            <SelectItem value="ACCOUNTANT">
                              Accountant
                            </SelectItem>
                            <SelectItem value="STAFF">Staff</SelectItem>
                            <SelectItem value="FACULTY">Faculty</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* College dropdown - only show for non-SYSTEM_ADMIN roles and only show for system admins (hide for college admins) */}
                  {editForm.watch("role") &&
                    editForm.watch("role") !== "SYSTEM_ADMIN" &&
                    !isCollegeAdmin && (
                      <FormField
                        control={editForm.control}
                        name="collegeId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>College *</FormLabel>
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select college" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {colleges.map((college) => (
                                  <SelectItem
                                    key={college.id}
                                    value={college.id}
                                  >
                                    <div>
                                      <div>{college.name}</div>
                                      <div className="text-xs text-gray-500">
                                        {college.code}
                                      </div>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                  <FormField
                    control={editForm.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select
                          value={field.value ? "active" : "inactive"}
                          onValueChange={(value) =>
                            field.onChange(value === "active")
                          }
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Updating..." : "Update User"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Users Table */}
        <div className="rounded-md border bg-white">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : (
                        <div
                          className={cn(
                            "flex items-center space-x-2",
                            header.column.getCanSort() &&
                              "cursor-pointer select-none hover:bg-gray-50"
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <div className="flex flex-col items-center justify-center w-4 h-4">
                              {header.column.getIsSorted() === "desc" ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : header.column.getIsSorted() === "asc" ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <div className="h-4 w-4" />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="hover:bg-gray-50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className={cn("px-4 py-2")}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {isLoading ? (
                      <Loader variant="loading-dots" text="Loading" />
                    ) : searchTerm ? (
                      "No users found matching your search."
                    ) : (
                      "No users available."
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {localPagination.totalPages > 0 && (
          <div className="flex items-center justify-between gap-8">
            {/* Results per page */}
            <div className="flex items-center gap-3">
              <Label htmlFor={id} className="max-sm:sr-only">
                Rows per page
              </Label>
              <Select
                value={table.getState().pagination.pageSize.toString()}
                onValueChange={(value) => {
                  table.setPageSize(Number(value));
                }}
              >
                <SelectTrigger id={id} className="w-fit whitespace-nowrap">
                  <SelectValue placeholder="Select number of results" />
                </SelectTrigger>
                <SelectContent className="[&_*[role=option]>span]:end-2 [&_*[role=option]>span]:start-auto [&_*[role=option]]:pe-8 [&_*[role=option]]:ps-2">
                  {[5, 10, 25, 50].map((pageSize) => (
                    <SelectItem key={pageSize} value={pageSize.toString()}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Page number information */}
            <div className="flex grow justify-end whitespace-nowrap text-sm text-muted-foreground">
              <p
                className="whitespace-nowrap text-sm text-muted-foreground"
                aria-live="polite"
              >
                <span className="text-foreground">
                  {table.getState().pagination.pageIndex *
                    table.getState().pagination.pageSize +
                    1}
                  -
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize,
                    localPagination?.total || 0
                  )}
                </span>{" "}
                of{" "}
                <span className="text-foreground">
                  {localPagination?.total || 0}
                </span>
              </p>
            </div>

            {/* Pagination buttons */}
            <div className="flex items-center gap-2">
              {/* First page button */}
              <Button
                size="icon"
                variant="outline"
                className="disabled:pointer-events-none disabled:opacity-50"
                onClick={() => table.firstPage()}
                disabled={!table.getCanPreviousPage()}
                aria-label="Go to first page"
              >
                <ChevronFirst className="stroke-2 h-4 w-4" aria-hidden="true" />
              </Button>
              {/* Previous page button */}
              <Button
                size="icon"
                variant="outline"
                className="disabled:pointer-events-none disabled:opacity-50"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                aria-label="Go to previous page"
              >
                <ChevronLeft className="stroke-2 h-4 w-4" aria-hidden="true" />
              </Button>
              {/* Next page button */}
              <Button
                size="icon"
                variant="outline"
                className="disabled:pointer-events-none disabled:opacity-50"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                aria-label="Go to next page"
              >
                <ChevronRight className="stroke-2 h-4 w-4" aria-hidden="true" />
              </Button>
              {/* Last page button */}
              <Button
                size="icon"
                variant="outline"
                className="disabled:pointer-events-none disabled:opacity-50"
                onClick={() => table.lastPage()}
                disabled={!table.getCanNextPage()}
                aria-label="Go to last page"
              >
                <ChevronLast className="stroke-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Users;
